import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { adminApi } from "../../../lib/admin";
import type { PropertyPredicateDto } from "../../../types/gigTypes";
import { endOfLocalDay, parseLocalDateInput, startOfLocalDay, toIsoTimestamp } from "../../../utils/DateUtils";
import { hasRole } from "../../../utils/AuthUtils";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import Button from "../../ui/Button/Button";
import Modal from "../../ui/Modal/Modal";
import AdminUsersTable from "./AdminUsersTable";

const DEFAULT_PAGE_SIZE = 10;

const AdminUsersPanel = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [state, setState] = useState("");
  const [under18, setUnder18] = useState(false);
  const [skillSearch, setSkillSearch] = useState("");
  const [role, setRole] = useState("");
  const [accountType, setAccountType] = useState("");
  const [teenPayoutSetup, setTeenPayoutSetup] = useState("");
  const [status, setStatus] = useState("");
  const [emailVerified, setEmailVerified] = useState("");
  const [guardianConnection, setGuardianConnection] = useState("");
  const [createdFromDate, setCreatedFromDate] = useState("");
  const [createdToDate, setCreatedToDate] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const [totalCountResult, setTotalCountResult] = useState<{ predicateSignature: string; totalCount: number } | null>(null);
  const [isCountModalOpen, setIsCountModalOpen] = useState(false);
  const [isEmailListModalOpen, setIsEmailListModalOpen] = useState(false);
  const [emailListCopyFeedback, setEmailListCopyFeedback] = useState<{ predicateSignature: string; message: string } | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedLocationSearch = useDebouncedValue(locationSearch, 300);
  const debouncedSkillSearch = useDebouncedValue(skillSearch, 300);
  const canWrite = hasRole(user?.roles, "ADMIN_WRITE");
  const predicates = useMemo(() => {
    const nextPredicates: PropertyPredicateDto[] = [];
    const createdFrom = parseLocalDateInput(createdFromDate);
    const createdTo = parseLocalDateInput(createdToDate);
    if (debouncedSearch.trim().length > 0) {
      nextPredicates.push({ propertyName: "search", propertyValue: debouncedSearch.trim(), operation: "CONTAINS" });
    }
    if (debouncedLocationSearch.trim().length > 0) {
      nextPredicates.push({ propertyName: "locationSearch", propertyValue: debouncedLocationSearch.trim(), operation: "CONTAINS" });
    }
    if (state) {
      nextPredicates.push({ propertyName: "state", propertyValue: state, operation: "EQUALS" });
    }
    if (under18) {
      nextPredicates.push({ propertyName: "under18", propertyValue: "true", operation: "EQUALS" });
    }
    if (debouncedSkillSearch.trim().length > 0) {
      nextPredicates.push({ propertyName: "skillSearch", propertyValue: debouncedSkillSearch.trim(), operation: "CONTAINS" });
    }
    if (role.trim().length > 0) {
      nextPredicates.push({ propertyName: "role", propertyValue: role.trim(), operation: "EQUALS" });
    }
    if (accountType.trim().length > 0) {
      nextPredicates.push({ propertyName: "accountType", propertyValue: accountType.trim(), operation: "EQUALS" });
    }
    if (teenPayoutSetup.trim().length > 0) {
      nextPredicates.push({ propertyName: "teenPayoutSetup", propertyValue: teenPayoutSetup.trim(), operation: "EQUALS" });
    }
    if (status.trim().length > 0) {
      nextPredicates.push({ propertyName: "status", propertyValue: status.trim(), operation: "EQUALS" });
    }
    if (emailVerified.trim().length > 0) {
      nextPredicates.push({ propertyName: "emailVerified", propertyValue: emailVerified.trim(), operation: "EQUALS" });
    }
    if (guardianConnection.trim().length > 0) {
      nextPredicates.push({ propertyName: "hasGuardianConnection", propertyValue: guardianConnection.trim(), operation: "EQUALS" });
    }
    if (createdFrom) {
      nextPredicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(startOfLocalDay(createdFrom)), operation: "TIMESTAMP_FROM" });
    }
    if (createdTo) {
      nextPredicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(endOfLocalDay(createdTo)), operation: "TIMESTAMP_TO" });
    }
    return nextPredicates;
  }, [accountType, createdFromDate, createdToDate, debouncedLocationSearch, debouncedSearch, debouncedSkillSearch, emailVerified, guardianConnection, role, state, status, teenPayoutSetup, under18]);

  const predicateSignature = useMemo(() => JSON.stringify(predicates), [predicates]);
  const totalCount = totalCountResult?.predicateSignature === predicateSignature ? totalCountResult.totalCount : null;
  const visibleEmailListCopyFeedback = emailListCopyFeedback?.predicateSignature === predicateSignature
    ? emailListCopyFeedback.message
    : null;

  const appliedFilters = useMemo(() => {
    const filters: Array<{ label: string; value: string }> = [];
    if (debouncedSearch.trim().length > 0) {
      filters.push({ label: "Search", value: debouncedSearch.trim() });
    }
    if (debouncedLocationSearch.trim().length > 0) {
      filters.push({ label: "Location", value: debouncedLocationSearch.trim() });
    }
    if (state) {
      filters.push({ label: "State", value: state });
    }
    if (under18) {
      filters.push({ label: "Age", value: "Teens under 18" });
    }
    if (debouncedSkillSearch.trim().length > 0) {
      filters.push({ label: "Teen skills", value: debouncedSkillSearch.trim() });
    }
    if (role.trim().length > 0) {
      filters.push({ label: "Role", value: role.trim() });
    }
    if (accountType.trim().length > 0) {
      filters.push({ label: "Account", value: accountType.trim() });
    }
    if (teenPayoutSetup.trim().length > 0) {
      filters.push({ label: "Stripe", value: teenPayoutSetup === "READY" ? "Teen ready" : "Teen not ready" });
    }
    if (status.trim().length > 0) {
      filters.push({ label: "Status", value: status.trim() });
    }
    if (emailVerified.trim().length > 0) {
      filters.push({ label: "Email", value: emailVerified === "true" ? "Verified" : "Unverified" });
    }
    if (guardianConnection.trim().length > 0) {
      filters.push({ label: "Guardian/Teen connection", value: guardianConnection === "true" ? "Has connection" : "No connection" });
    }
    if (createdFromDate.trim().length > 0) {
      filters.push({ label: "Created from", value: createdFromDate });
    }
    if (createdToDate.trim().length > 0) {
      filters.push({ label: "Created to", value: createdToDate });
    }
    return filters;
  }, [accountType, createdFromDate, createdToDate, debouncedLocationSearch, debouncedSearch, debouncedSkillSearch, emailVerified, guardianConnection, role, state, status, teenPayoutSetup, under18]);

  const totalCountMutation = useMutation({
    mutationFn: async () => {
      const requestPredicateSignature = predicateSignature;
      const response = await adminApi.countUsers({
        predicates,
        page: {
          number: 0,
          size: pageSize,
          numberOfElements: null,
          hasNext: false,
          hasPrevious: false,
          first: false,
          last: false,
          sortBy: [{ direction: "DESC", propertyName: "createdAt" }],
        },
      });
      return { predicateSignature: requestPredicateSignature, response };
    },
    onSuccess: ({ predicateSignature: requestPredicateSignature, response }) => {
      setTotalCountResult({ predicateSignature: requestPredicateSignature, totalCount: response.totalCount });
    },
  });

  const emailListMutation = useMutation({
    mutationFn: () =>
      adminApi.generateUserEmailList({
        predicates,
        page: {
          number: 0,
          size: pageSize,
          numberOfElements: null,
          hasNext: false,
          hasPrevious: false,
          first: false,
          last: false,
          sortBy: [{ direction: "DESC", propertyName: "createdAt" }],
        },
      }),
    onSuccess: () => {
      setEmailListCopyFeedback(null);
    },
  });

  const openEmailListModal = () => {
    setIsEmailListModalOpen(true);
    emailListMutation.mutate();
  };

  const copyEmailList = async () => {
    const emailList = emailListMutation.data?.commaSeparatedEmails ?? "";
    if (!emailList) {
      return;
    }
    try {
      await navigator.clipboard.writeText(emailList);
      setEmailListCopyFeedback({ predicateSignature, message: "Copied email list." });
    } catch {
      setEmailListCopyFeedback({ predicateSignature, message: "Copy failed. Select the text and copy it manually." });
    }
  };

  const usersQuery = useQuery({
    queryKey: ["admin-users", { page, pageSize, debouncedSearch, debouncedLocationSearch, state, under18, debouncedSkillSearch, role, accountType, teenPayoutSetup, status, emailVerified, guardianConnection, createdFromDate, createdToDate }],
    queryFn: () =>
      adminApi.listUsers({
        predicates,
        page: {
          number: page,
          size: pageSize,
          numberOfElements: null,
          hasNext: false,
          hasPrevious: false,
          first: false,
          last: false,
          sortBy: [{ direction: "DESC", propertyName: "createdAt" }],
        },
      }),
    placeholderData: (previousData) => previousData,
  });

  const error = usersQuery.error
    ? apiErrorToMessage(usersQuery.error, "Failed to load users.")
    : totalCountMutation.error
      ? apiErrorToMessage(totalCountMutation.error, "Failed to load the filtered user count.")
      : emailListMutation.error
        ? apiErrorToMessage(emailListMutation.error, "Failed to generate the email list.")
        : null;

  return (
    <div>
      {error ? <p className="mb-4 text-sm font-medium text-red-600">{error}</p> : null}
      <AdminUsersTable
        users={usersQuery.data?.users ?? []}
        isLoading={usersQuery.isLoading}
        canWrite={canWrite}
        search={search}
        locationSearch={locationSearch}
        state={state}
        under18={under18}
        skillSearch={skillSearch}
        role={role}
        accountType={accountType}
        teenPayoutSetup={teenPayoutSetup}
        status={status}
        emailVerified={emailVerified}
        guardianConnection={guardianConnection}
        createdFromDate={createdFromDate}
        createdToDate={createdToDate}
        pageSize={pageSize}
        page={page}
        resultsCount={usersQuery.data?.page.numberOfElements ?? 0}
        hasNextPage={usersQuery.data?.page.hasNext ?? false}
        hasPreviousPage={usersQuery.data?.page.hasPrevious ?? false}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        onLocationSearchChange={(value) => {
          setLocationSearch(value);
          setPage(0);
        }}
        onStateChange={(value) => {
          setState(value);
          setPage(0);
        }}
        onUnder18Change={(value) => {
          setUnder18(value);
          setPage(0);
        }}
        onSkillSearchChange={(value) => {
          setSkillSearch(value);
          setPage(0);
        }}
        onRoleChange={(value) => {
          setRole(value);
          setPage(0);
        }}
        onAccountTypeChange={(value) => {
          setAccountType(value);
          setPage(0);
        }}
        onTeenPayoutSetupChange={(value) => {
          setTeenPayoutSetup(value);
          setPage(0);
        }}
        onStatusChange={(value) => {
          setStatus(value);
          setPage(0);
        }}
        onEmailVerifiedChange={(value) => {
          setEmailVerified(value);
          setPage(0);
        }}
        onGuardianConnectionChange={(value) => {
          setGuardianConnection(value);
          setPage(0);
        }}
        onCreatedFromDateChange={(value) => {
          setCreatedFromDate(value);
          setPage(0);
        }}
        onCreatedToDateChange={(value) => {
          setCreatedToDate(value);
          setPage(0);
        }}
        onPageSizeChange={(value) => {
          setPageSize(value);
          setPage(0);
        }}
        onPageChange={setPage}
        onRequestTotalCount={() => setIsCountModalOpen(true)}
        onGenerateEmailList={openEmailListModal}
      />
      <Modal isOpen={isCountModalOpen} onClose={() => setIsCountModalOpen(false)} title="Get total count" panelClassName="max-w-xl">
        <div className="space-y-5">
          <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
            <p className="text-sm text-myteengig-text-primary">
              This action will count users that match the filters currently applied on the page.
            </p>
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Applied filters</p>
            {appliedFilters.length > 0 ? (
              <div className="mt-3 space-y-2">
                {appliedFilters.map((filter) => (
                  <div key={filter.label} className="flex items-start justify-between gap-4 border-b border-myteengig-border/70 pb-2 last:border-b-0 last:pb-0">
                    <span className="text-sm font-medium text-myteengig-text-muted">{filter.label}</span>
                    <span className="text-right text-sm text-myteengig-text-primary">{filter.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-myteengig-text-muted">No filters are applied. This will count all users.</p>
            )}
          </div>

          {totalCount !== null ? (
            <div className="rounded-2xl border border-myteengig-border bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Total users</p>
              <p className="mt-2 text-2xl font-semibold text-myteengig-text-primary">{totalCount}</p>
            </div>
          ) : null}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setIsCountModalOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => totalCountMutation.mutate()} disabled={totalCountMutation.isPending}>
              {totalCountMutation.isPending ? "Counting..." : "Run count"}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal isOpen={isEmailListModalOpen} onClose={() => setIsEmailListModalOpen(false)} title="Generate email list" panelClassName="max-w-2xl">
        <div className="space-y-5">
          <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
            <p className="text-sm text-myteengig-text-primary">
              This generates a comma-separated list of user emails matching the filters currently applied on the page.
            </p>
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Applied filters</p>
            {appliedFilters.length > 0 ? (
              <div className="mt-3 space-y-2">
                {appliedFilters.map((filter) => (
                  <div key={filter.label} className="flex items-start justify-between gap-4 border-b border-myteengig-border/70 pb-2 last:border-b-0 last:pb-0">
                    <span className="text-sm font-medium text-myteengig-text-muted">{filter.label}</span>
                    <span className="text-right text-sm text-myteengig-text-primary">{filter.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-myteengig-text-muted">No filters are applied. This will include all users with an email address.</p>
            )}
          </div>

          <div className="rounded-2xl border border-myteengig-border bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Matching emails</p>
                <p className="mt-2 text-2xl font-semibold text-myteengig-text-primary">
                  {emailListMutation.isPending ? "..." : emailListMutation.data?.totalCount ?? 0}
                </p>
              </div>
              <Button type="button" variant="outline" onClick={() => emailListMutation.mutate()} disabled={emailListMutation.isPending}>
                {emailListMutation.isPending ? "Generating..." : "Refresh"}
              </Button>
            </div>
            <textarea
              readOnly
              rows={6}
              value={emailListMutation.data?.commaSeparatedEmails ?? ""}
              className="mt-4 w-full rounded-lg border border-myteengig-border bg-myteengig-background/40 px-4 py-3 text-sm text-myteengig-text-primary shadow-sm outline-none"
              placeholder={emailListMutation.isPending ? "Generating email list..." : "No emails found."}
            />
            {visibleEmailListCopyFeedback ? <p className="mt-3 text-sm text-myteengig-text-muted">{visibleEmailListCopyFeedback}</p> : null}
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setIsEmailListModalOpen(false)}>
              Close
            </Button>
            <Button
              type="button"
              onClick={copyEmailList}
              disabled={emailListMutation.isPending || !emailListMutation.data?.commaSeparatedEmails}
            >
              Copy list
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminUsersPanel;
